import React, { useEffect, useState, useCallback } from 'react';
import { Clock, Send, Check, BellOff, X, RefreshCw, Copy } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const FU_TREND_STYLES = {
  ADVANCING: 'bg-green-100 text-green-700',
  STEADY: 'bg-neutral-100 text-neutral-600',
  STALLED: 'bg-amber-100 text-amber-700',
  COOLING: 'bg-orange-100 text-orange-700',
  LOST: 'bg-red-100 text-red-700',
};

const REASON_LABEL = {
  HIGH_INTENT_NO_ORDER: 'High intent, no order yet',
  PRODUCT_ENQUIRY_UNRESOLVED: 'Product enquiry unresolved',
  PRICE_ENQUIRY_UNRESOLVED: 'Price enquiry unresolved',
  MOQ_ENQUIRY_UNRESOLVED: 'MOQ enquiry unresolved',
  STOCK_ENQUIRY_UNRESOLVED: 'Stock enquiry unresolved',
  NEED_MORE_TIME: 'Buyer needs more time',
  NOT_REQUIRED_NOW: 'Not required right now',
  PAYMENT_PENDING: 'Payment pending',
  REPEAT_CUSTOMER_FOLLOW_UP: 'Repeat customer',
  NEW_ENQUIRY_UNRESOLVED: 'New enquiry unresolved',
  PRODUCT_CODE_ENQUIRY: 'Product code enquiry',
};

export default function SellerFollowups() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/seller/followups');
      setData(res.data);
    } catch (e) {
      toast.error('Could not load follow-ups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (buyerKey, action, extra = {}) => {
    setBusy(`${buyerKey}:${action}`);
    try {
      await api.post(`/seller/followups/${encodeURIComponent(buyerKey)}/action`, { action, ...extra });
      toast.success(
        action === 'sent' ? 'Marked as sent by you'
          : action === 'done' ? 'Marked done'
          : action === 'snooze' ? 'Snoozed for 3 days'
          : 'Dismissed');
      await load();
    } catch (e) {
      toast.error('Action failed');
    } finally {
      setBusy('');
    }
  };

  const sendMyself = async (item) => {
    // SELLER IS THE ACTOR. We only copy the suggested text for the seller to use
    // in their own channel, then record that they actioned it. No auto-send.
    try {
      await navigator.clipboard.writeText(item.suggested_message || '');
      toast.success('Message copied — send it from your own chat/WhatsApp');
    } catch {
      toast.message('Copy the message below and send it yourself');
    }
    await act(item.buyer_key, 'sent');
  };

  const items = data?.followups || [];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="seller-followups-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900" data-testid="followups-title">Follow-ups</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Suggested follow-ups for your buyers. You send them yourself — nothing goes out automatically.
          </p>
        </div>
        <button onClick={load} data-testid="followups-refresh"
          className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-neutral-200 hover:bg-neutral-50">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-neutral-400 text-sm py-16 text-center" data-testid="followups-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-neutral-400 text-sm py-16 text-center border border-dashed border-neutral-200 rounded-xl"
          data-testid="followups-empty">
          No follow-ups due right now.
        </div>
      ) : (
        <div className="space-y-4" data-testid="followups-list">
          <div className="text-xs text-neutral-500" data-testid="followups-count">{items.length} follow-up(s) due</div>
          {items.map((it) => (
            <div key={it.buyer_key} data-testid={`followup-card-${it.buyer_key}`}
              className="border border-neutral-200 rounded-xl p-4 bg-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-semibold text-neutral-900 flex items-center gap-2" data-testid={`followup-buyer-${it.buyer_key}`}>
                    {it.buyer_name || it.buyer_phone || 'Buyer'}
                    {it.trend?.trend && (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${FU_TREND_STYLES[it.trend.trend] || 'bg-neutral-100 text-neutral-600'}`}
                        title={it.trend.evidence?.reason || ''} data-testid={`followup-trend-${it.buyer_key}`}>
                        {it.trend.trend}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {REASON_LABEL[it.follow_up_candidate_type] || it.follow_up_candidate_type || 'Follow-up'}
                    {it.current_intent ? ` · ${it.current_intent}` : ''}
                  </div>
                </div>
                {it.follow_up_target_at && (
                  <div className="inline-flex items-center gap-1 text-xs text-neutral-400 whitespace-nowrap">
                    <Clock size={13} /> due {String(it.follow_up_target_at).slice(0, 10)}
                  </div>
                )}
              </div>

              <div className="mt-3 rounded-lg bg-neutral-50 border border-neutral-100 p-3 text-sm text-neutral-700"
                data-testid={`followup-message-${it.buyer_key}`}>
                {it.suggested_message}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={() => sendMyself(it)} disabled={busy.startsWith(it.buyer_key)}
                  data-testid={`followup-send-${it.buyer_key}`}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50">
                  <Send size={14} /> Send myself
                </button>
                <button onClick={() => act(it.buyer_key, 'done')} disabled={busy.startsWith(it.buyer_key)}
                  data-testid={`followup-done-${it.buyer_key}`}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 disabled:opacity-50">
                  <Check size={14} /> Mark done
                </button>
                <button onClick={() => act(it.buyer_key, 'snooze', { snooze_days: 3 })} disabled={busy.startsWith(it.buyer_key)}
                  data-testid={`followup-snooze-${it.buyer_key}`}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 disabled:opacity-50">
                  <BellOff size={14} /> Snooze
                </button>
                <button onClick={() => act(it.buyer_key, 'dismiss')} disabled={busy.startsWith(it.buyer_key)}
                  data-testid={`followup-dismiss-${it.buyer_key}`}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-neutral-200 text-neutral-500 hover:bg-neutral-50 disabled:opacity-50">
                  <X size={14} /> Dismiss
                </button>
                <button onClick={() => navigator.clipboard.writeText(it.suggested_message || '').then(() => toast.success('Copied'))}
                  data-testid={`followup-copy-${it.buyer_key}`}
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50">
                  <Copy size={14} /> Copy
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
